import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const initialState = {
  roles: [],
  permissions: [],
  getRolesLoading: false,
  getRolesError: null,
  getPermissionsLoading: false,
  getPermissionsError: null,
  createRoleLoading: false,
  createRoleError: null,
  viewRoleLoading: false,
  viewRoleError: null,
  selectedRole: null,
  updateRoleLoading: false,
  updateRoleError: null,
  deleteRoleLoading: false,
  deleteRoleError: null,
};

export const fetchRoles = createAsyncThunk(
  "roles/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("OrviSlideToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/roles`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to fetch roles",
        );
      }
      const data = await response.json();
      const rolesData = data.data;
      return rolesData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPermissions = createAsyncThunk(
  "roles/fetchPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("OrviSlideToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/permissions`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to fetch permissions",
        );
      }
      const data = await response.json();
      const permissionsData = data.data;
      return permissionsData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createRole = createAsyncThunk(
  "roles/createRole",
  async (roleData, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/roles`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(roleData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const viewRole = createAsyncThunk(
  "roles/viewRole",
  async (roleId, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/roles/${roleId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to view role",
        );
      }
      const data = await response.json();
      const roleData = data.data;
      return roleData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateRole = createAsyncThunk(
  "roles/updateRole",
  async ({ roleData, roleId }, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/roles/${roleId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(roleData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteRole = createAsyncThunk(
  "roles/deleteRole",
  async (roleId, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/roles/${roleId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to delete role",
        );
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch roles
      .addCase(fetchRoles.pending, (state) => {
        state.getRolesLoading = true;
        state.getRolesError = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.getRolesLoading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.getRolesLoading = false;
        state.getRolesError = action.payload;
      })
      // Fetch permissions
      .addCase(fetchPermissions.pending, (state) => {
        state.getPermissionsLoading = true;
        state.getPermissionsError = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.getPermissionsLoading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.getPermissionsLoading = false;
        state.getPermissionsError = action.payload;
      })
      // create
      .addCase(createRole.pending, (state) => {
        state.createRoleLoading = true;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.createRoleLoading = false;
        state.roles.push(action.payload);
      })
      .addCase(createRole.rejected, (state, action) => {
        state.createRoleLoading = false;
        state.createRoleError = action.payload;
      })
      // view
      .addCase(viewRole.pending, (state) => {
        state.viewRoleLoading = true;
        state.viewRoleError = null;
      })
      .addCase(viewRole.fulfilled, (state, action) => {
        state.viewRoleLoading = false;
        state.selectedRole = action.payload;
      })
      .addCase(viewRole.rejected, (state, action) => {
        state.viewRoleLoading = false;
        state.viewRoleError = action.payload;
      })
      //  Update
      .addCase(updateRole.pending, (state) => {
        state.updateRoleLoading = true;
        state.updateRoleError = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.updateRoleLoading = false;
        const updatedRole = action.payload;
        state.roles = state.roles.map((role) =>
          role.id === updatedRole.id ? updatedRole : role,
        );
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.updateRoleLoading = false;
        state.updateRoleError = action.payload;
      })
      // delete
      .addCase(deleteRole.pending, (state) => {
        state.deleteRoleLoading = true;
        state.deleteRoleError = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.deleteRoleLoading = false;
        const deletedId = action.payload.id;
        state.roles = state.roles.filter((admin) => admin.id !== deletedId);
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.deleteRoleLoading = false;
        state.deleteRoleError = action.payload;
      });
  },
});

export default rolesSlice.reducer;
